import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Check, X, User, Clock, Gavel, MessageCircle, Building2, Phone, Send, Trash2, Plus, Hand, Edit, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getPhotoUrl } from '@/utils/photoUrl';
import { useAuth } from '@/contexts/AuthContext';
import { isActiveRevendedor, isReenviarCandidate } from '@/utils/revendedores';
import NewManualOfferDialog from '@/components/ofertas/NewManualOfferDialog';
import { ImageGallery } from '@/components/ui/image-gallery';
import MarcaModeloSelector from '@/components/MarcaModeloSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Borrador: 'secondary',
  Enviada: 'default',
  Adjudicada: 'outline',
  Pendiente: 'default',
  Aceptada: 'outline',
  Rechazada: 'destructive',
  'En espera': 'secondary',
  'No adjudicada': 'outline',
};

const DetallePublicacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.rol === 'SuperAdmin' || user?.isSuperAdmin;
  const isAdmin = user?.rol === 'Admin' || isSuperAdmin;
  const [vehiculo, setVehiculo] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [revendedores, setRevendedores] = useState({});
  const [sucursales, setSucursales] = useState({});
  const [loading, setLoading] = useState(true);
  const [ofertaToAccept, setOfertaToAccept] = useState(null);
  const [ofertaToReject, setOfertaToReject] = useState(null);
  const [selectedRevendedor, setSelectedRevendedor] = useState(null);

  const [showReenviarDialog, setShowReenviarDialog] = useState(false);
  const [selectedRevendedoresReenviar, setSelectedRevendedoresReenviar] = useState([]);
  const [reenviando, setReenviando] = useState(false);

  const [mostrarBorradorDialog, setMostrarBorradorDialog] = useState(false);
  const [comentarioNoAdjudicada, setComentarioNoAdjudicada] = useState('');
  const [showNoAdjudicadoDialog, setShowNoAdjudicadoDialog] = useState(false);
  const [showEsperaDialog, setShowEsperaDialog] = useState(false);
  const [motivoEspera, setMotivoEspera] = useState('');
  const [fechaEspera, setFechaEspera] = useState('');

  const [showNewOfferDialog, setShowNewOfferDialog] = useState(false);
  const [processingOffer, setProcessingOffer] = useState(null); // Track which offer is being processed

  const [showInteraccionesDialog, setShowInteraccionesDialog] = useState(false);
  const [interaccionesData, setInteraccionesData] = useState([]);
  const [loadingInteracciones, setLoadingInteracciones] = useState(false);
  const [searchInteraccion, setSearchInteraccion] = useState('');

  const [showEditMarcaModelo, setShowEditMarcaModelo] = useState(false);
  const [editMarca, setEditMarca] = useState('');
  const [editModelo, setEditModelo] = useState('');
  const [savingMarcaModelo, setSavingMarcaModelo] = useState(false);
  
  const [showAdjudicarDialog, setShowAdjudicarDialog] = useState(false);

  const [showExtenderDialog, setShowExtenderDialog] = useState(false);
  const [horasExtender, setHorasExtender] = useState(1);
  const [extendiendo, setExtendiendo] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [vehiculoRes, ofertasRes, revendedoresRes, sucursalesRes] = await Promise.all([
        axios.get(`${API}/vehiculos/${id}`),
        axios.get(`${API}/ofertas/vehiculo/${id}?includeDeleted=true`),
        axios.get(`${API}/revendedores?includeDeleted=true`),
        axios.get(`${API}/sucursales`),
      ]);

      setVehiculo(vehiculoRes.data);
      setOfertas(ofertasRes.data);

      const revMap = {};
      revendedoresRes.data.forEach((r) => {
        revMap[r.id] = r;
      });
      setRevendedores(revMap);

      const sucMap = {};
      sucursalesRes.data.forEach((s) => {
        sucMap[s.id] = s;
      });
      setSucursales(sucMap);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || 'No tienes permisos para ver esta publicación eliminada');
        navigate('/publicaciones');
        return;
      }
      toast.error('Error al cargar detalle');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInteraccionesModal = async () => {
    setShowInteraccionesDialog(true);
    setSearchInteraccion('');
    try {
      setLoadingInteracciones(true);
      const res = await axios.get(`${API}/vehiculos/${id}/interacciones`);
      setInteraccionesData(res.data.interacciones || []);
    } catch (error) {
      console.error('Error loading interacciones:', error);
      toast.error('Error al cargar revendedores que interactuaron');
    } finally {
      setLoadingInteracciones(false);
    }
  };

  const filteredInteracciones = interaccionesData.filter(item => {
    if (!searchInteraccion) return true;
    const q = searchInteraccion.toLowerCase();
    const nombre = (item.revendedor?.nombre || item.nombreContacto || '').toLowerCase();
    const empresa = (item.revendedor?.empresa || '').toLowerCase();
    const telefono = (item.telefono || '').toLowerCase();
    return nombre.includes(q) || empresa.includes(q) || telefono.includes(q);
  });

  // handleAcceptOffer removed as logic moved to handlePasarAEspera

  const handleRejectOffer = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      loadData();
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    } finally {
      setProcessingOffer(null);
    }
    setOfertaToReject(null);
  };

  const handleSuperAdminStatusChange = async (newStatus) => {
    if (newStatus === 'En espera') {
      const defaultDate = new Date();
      defaultDate.setHours(defaultDate.getHours() + 24);
      const tzoffset = defaultDate.getTimezoneOffset() * 60000;
      const localISOTime = new Date(defaultDate - tzoffset).toISOString().slice(0, 16);
      setFechaEspera(localISOTime);
      setMotivoEspera('');
      setShowEsperaDialog(true);
      return;
    }
    if (newStatus === 'No adjudicada') {
      setComentarioNoAdjudicada('');
      setShowNoAdjudicadoDialog(true);
      return;
    }

    try {
      await axios.put(`${API}/vehiculos/${id}`, { estadoPublicacion: newStatus });
      toast.success('Estado del vehículo actualizado');
      loadData();
    } catch (error) {
      console.error('Error updating vehicle status:', error);
      toast.error('Error al actualizar estado del vehículo');
    }
  };

  const handleSuperAdminOfferStatusChange = async (ofertaId, newStatus) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: newStatus });
      toast.success('Estado de la oferta actualizado');
      loadData();
    } catch (error) {
      console.error('Error updating offer status:', error);
      toast.error('Error al actualizar estado de la oferta');
    }
  };

  const handleSoftDeleteOffer = async (ofertaId) => {
    if (!window.confirm("¿Está seguro de que desea mover esta oferta a la papelera?")) return;
    try {
      await axios.delete(`${API}/ofertas/${ofertaId}`);
      toast.success('Oferta movida a la papelera');
      loadData();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar oferta');
    }
  };

  const handleRestoreOffer = async (ofertaId) => {
    try {
      await axios.patch(`${API}/ofertas/${ofertaId}/restore`);
      toast.success('Oferta restaurada');
      loadData();
    } catch (error) {
      console.error('Error restoring offer:', error);
      toast.error(error.response?.data?.detail || 'Error al restaurar oferta');
    }
  };

  const handlePermanentDeleteOffer = async (ofertaId) => {
    if (!window.confirm("¿Está seguro de que desea eliminar DEFINITIVAMENTE esta oferta? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/ofertas/${ofertaId}/permanent`);
      toast.success('Oferta eliminada definitivamente');
      loadData();
    } catch (error) {
      console.error('Error permanently deleting offer:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar oferta definitivamente');
    }
  };

  const handleRestoreVehiculo = async () => {
    try {
      await axios.patch(`${API}/vehiculos/${id}/restore`);
      toast.success('Publicación restaurada con éxito');
      loadData();
    } catch (error) {
      console.error('Error restoring vehiculo:', error);
      toast.error(error.response?.data?.detail || 'Error al restaurar publicación');
    }
  };

  const handlePermanentDeleteVehiculo = async () => {
    if (!window.confirm("¿Está seguro de que desea eliminar DEFINITIVAMENTE esta publicación y todas sus ofertas? Esta acción es irreversible.")) return;
    try {
      await axios.delete(`${API}/vehiculos/${id}/permanent`);
      toast.success('Publicación eliminada definitivamente');
      navigate('/publicaciones');
    } catch (error) {
      console.error('Error permanently deleting vehiculo:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar publicación');
    }
  };

  const handlePasarAEspera = async () => {
    if (!motivoEspera || !fechaEspera) {
      toast.error('La fecha y el motivo son obligatorios');
      return;
    }
    try {
      const isoDate = new Date(fechaEspera).toISOString();
      if (ofertaToAccept) {
        setProcessingOffer(ofertaToAccept);
        await axios.put(`${API}/ofertas/${ofertaToAccept}`, { 
          estado: 'Aceptada',
          fechaEspera: isoDate,
          motivoEspera: motivoEspera
        });
        toast.success('Oferta aceptada y publicación en espera');
      } else {
        await axios.put(`${API}/vehiculos/${id}`, {
          estadoPublicacion: 'En espera',
          fechaEspera: isoDate,
          motivoEspera: motivoEspera
        });
        toast.success('Publicación en espera actualizada');
      }
      setShowEsperaDialog(false);
      setOfertaToAccept(null);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(ofertaToAccept ? 'Error al aceptar la oferta' : 'Error al actualizar publicación a En espera');
    } finally {
      if (ofertaToAccept) {
        setProcessingOffer(null);
      }
    }
  };

  const handleNoAdjudicado = async () => {
    // Comment is mandatory if publication is not expired (still active) or if expired but has offers
    const requiresComment = !isVencida || ofertas.length > 0;
    if (requiresComment && !comentarioNoAdjudicada.trim()) {
      toast.error('El comentario es obligatorio');
      return;
    }
    try {
      await axios.put(`${API}/vehiculos/${id}`, {
        estadoPublicacion: 'No adjudicada',
        comentarioNoAdjudicada: comentarioNoAdjudicada.trim() || undefined
      });
      toast.success('Publicación marcada como No adjudicada');
      setShowNoAdjudicadoDialog(false);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar publicación a No adjudicada');
    }
  };

  const handleAdjudicar = async () => {
    try {
      await axios.put(`${API}/vehiculos/${id}`, { estadoPublicacion: 'Adjudicada' });
      toast.success('Publicación adjudicada exitosamente');
      setShowAdjudicarDialog(false);
      loadData();
    } catch (error) {
      console.error('Error adjudicando:', error);
      toast.error(error.response?.data?.detail || 'Error al adjudicar la publicación');
    }
  };

  const handleSelectBidders = () => {
    const bidderIds = [...new Set(ofertas.map((o) => o.revendedorId))];
    const activeBidderIds = bidderIds.filter(id => {
      return isReenviarCandidate(revendedores[id]);
    });
    setSelectedRevendedoresReenviar(activeBidderIds);
  };

  const handleReenviar = async () => {
    if (selectedRevendedoresReenviar.length === 0) {
      toast.error('Selecciona al menos un revendedor');
      return;
    }

    setReenviando(true);
    try {
      const res = await axios.post(`${API}/vehiculos/${id}/reenviar`, {
        revendedorIds: selectedRevendedoresReenviar
      });
      toast.success(`Publicación reenviada a ${res.data.enviados} revendedores`);
      setShowReenviarDialog(false);
      setSelectedRevendedoresReenviar([]);
    } catch (error) {
      console.error('Error reenviando:', error);
      toast.error('Error al reenviar la publicación');
    } finally {
      setReenviando(false);
    }
  };

  const handleExtender = async () => {
    if (!horasExtender || horasExtender < 1) {
      toast.error('La cantidad de horas debe ser al menos 1');
      return;
    }

    setExtendiendo(true);
    try {
      await axios.post(`${API}/vehiculos/${id}/extender`, { horas: parseInt(horasExtender, 10) });
      toast.success(`Plazo extendido por ${horasExtender} horas exitosamente.`);
      setShowExtenderDialog(false);
      setHorasExtender(1);
      loadData();
    } catch (error) {
      console.error('Error extendiendo plazo:', error);
      toast.error(error.response?.data?.detail || 'Error al extender el plazo');
    } finally {
      setExtendiendo(false);
    }
  };

  const toggleRevendedorSelection = (revendedorId) => {
    setSelectedRevendedoresReenviar(prev =>
      prev.includes(revendedorId)
        ? prev.filter(rId => rId !== revendedorId)
        : [...prev, revendedorId]
    );
  };

  const formatWhatsappLink = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/[^0-9+]/g, '');
    return `https://wa.me/${cleaned.replace('+', '')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!vehiculo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="text-muted-foreground">Vehículo no encontrado</div>
        <Button onClick={() => navigate('/publicaciones')}>Volver</Button>
      </div>
    );
  }

  const sucursal = vehiculo.sucursalId ? sucursales[vehiculo.sucursalId] : null;
  const isVencida = vehiculo && vehiculo.fechaLimiteOfertas && new Date(vehiculo.fechaLimiteOfertas) < new Date() && !['Adjudicada', 'Borrador', 'En espera', 'No adjudicada'].includes(vehiculo.estadoPublicacion);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate('/publicaciones')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Publicaciones
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-semibold tracking-tight">
                  {vehiculo.marca} {vehiculo.modelo}
                </h1>
                {vehiculo.esSubasta && (
                  <Badge variant="secondary" className="gap-1">
                    <Gavel className="h-3 w-3" />
                    Subasta
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground mt-1 text-sm font-medium">
                {vehiculo.version && `${vehiculo.version} • `}{vehiculo.anio} • {vehiculo.km.toLocaleString()} km
                <span className="ml-2 text-xs opacity-60 font-normal">({vehiculo.id})</span>
              </p>

              {vehiculo.interaccionesWhatsapp > 0 && (
                <div className="mt-3 flex items-center">
                  <button
                    type="button"
                    onClick={handleOpenInteraccionesModal}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800 transition-all cursor-pointer group shadow-sm hover:shadow"
                    title="Haga click para ver la lista de revendedores que interactuaron"
                    data-testid="interacciones-whatsapp-button"
                  >
                    <MessageCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform" />
                    <span className="font-semibold">
                      {vehiculo.interaccionesWhatsapp} {vehiculo.interaccionesWhatsapp === 1 ? 'Revendedor Interactuó' : 'Revendedores Interactuaron'}
                    </span>
                    <span className="text-[11px] text-green-600/80 dark:text-green-400/80 ml-0.5 underline underline-offset-2">
                      (Ver quiénes)
                    </span>
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {vehiculo.deletedAt ? (
                <div className="flex items-center gap-2">
                  <Badge variant="destructive" className="gap-1.5 h-8 px-3 text-xs">
                    <Trash2 className="h-3.5 w-3.5" />
                    En Papelera
                  </Badge>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRestoreVehiculo}
                      className="gap-1.5 border-primary text-primary hover:bg-primary/10 h-8"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Restaurar
                    </Button>
                  )}
                  {isSuperAdmin && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handlePermanentDeleteVehiculo}
                      className="gap-1.5 h-8"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Eliminar definitivamente
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {(vehiculo.estadoPublicacion === 'Enviada' || vehiculo.estadoPublicacion === 'En espera') && (
                    <Button
                      onClick={() => setShowNewOfferDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Cargar Oferta Manual
                    </Button>
                  )}
                  {vehiculo.estadoPublicacion === 'Enviada' && !isVencida && (
                    <Button
                      variant="outline"
                      onClick={() => setShowReenviarDialog(true)}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Reenviar
                    </Button>
                  )}
                  {vehiculo.estadoPublicacion === 'Enviada' && (
                    <Button
                      variant="outline"
                      onClick={() => setShowExtenderDialog(true)}
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Extender Plazo
                    </Button>
                  )}
                  {vehiculo.estadoPublicacion === 'En espera' && (
                    <Button 
                      onClick={() => setShowAdjudicarDialog(true)} 
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Adjudicar
                    </Button>
                  )}
                  {(isVencida || vehiculo.estadoPublicacion === 'En espera' || vehiculo.estadoPublicacion === 'Enviada') && (
                    <Button variant="outline" className="text-muted-foreground" onClick={() => {
                      setComentarioNoAdjudicada('');
                      setShowNoAdjudicadoDialog(true);
                    }}>
                      No adjudicada
                    </Button>
                  )}
                  {(user?.rol === 'SuperAdmin' || user?.rol === 'Admin') ? (
                    <Select value={vehiculo.estadoPublicacion} onValueChange={handleSuperAdminStatusChange}>
                      <SelectTrigger className={`h-8 w-[140px] text-xs font-medium border-0 focus:ring-0 ${vehiculo.estadoPublicacion === 'Borrador' ? 'bg-secondary text-secondary-foreground' : vehiculo.estadoPublicacion === 'Rechazada' ? 'bg-destructive text-destructive-foreground' : vehiculo.estadoPublicacion === 'Enviada' ? 'bg-primary text-primary-foreground' : vehiculo.estadoPublicacion === 'Adjudicada' ? 'border bg-background' : 'bg-secondary text-secondary-foreground'}`}>
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Borrador">Borrador</SelectItem>
                        <SelectItem value="Enviada">Enviada</SelectItem>
                        <SelectItem value="Adjudicada">Adjudicada</SelectItem>
                        <SelectItem value="En espera">En espera</SelectItem>
                        <SelectItem value="No adjudicada">No adjudicada</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant={badgeVariants[vehiculo.estadoPublicacion]} data-testid="listing-status-badge">
                      {vehiculo.estadoPublicacion}
                    </Badge>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {vehiculo.deletedAt && (
          <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-destructive/15 flex items-center justify-center flex-shrink-0 text-destructive">
                  <Trash2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-destructive">Publicación en Papelera</h3>
                    <Badge variant="destructive" className="text-xs">Eliminada</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Esta publicación fue eliminada el {format(new Date(vehiculo.deletedAt), 'dd/MM/yyyy HH:mm', { locale: es })}.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRestoreVehiculo}
                    className="gap-1.5 border-primary text-primary hover:bg-primary/10"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Restaurar publicación
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handlePermanentDeleteVehiculo}
                    className="gap-1.5"
                  >
                    <Trash2 className="h-4 w-4" />
                    Eliminar definitivamente
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Left Column: Photos and Details */}
          <div className="space-y-6">
            {/* Image Carousel */}
            <Card data-testid="image-carousel">
              <CardContent className="p-6">
                <ImageGallery fotos={vehiculo.fotos} />
              </CardContent>
            </Card>

            {/* Vehicle Details */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Detalles del Vehículo</CardTitle>
                  {(user?.rol === 'Admin' || user?.rol === 'SuperAdmin') && !vehiculo.deletedAt && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditMarca(vehiculo.marca || '');
                        setEditModelo(vehiculo.modelo || '');
                        setShowEditMarcaModelo(true);
                      }}
                      className="gap-1.5 text-xs"
                      data-testid="edit-marca-modelo-button"
                    >
                      <Edit className="h-3.5 w-3.5" />
                      Editar Marca/Modelo
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Marca:</span>{' '}
                    <span className="font-medium">{vehiculo.marca}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Modelo:</span>{' '}
                    <span className="font-medium">{vehiculo.modelo}</span>
                  </div>
                  {vehiculo.version && (
                    <div>
                      <span className="text-muted-foreground">Versión:</span>{' '}
                      <span className="font-medium">{vehiculo.version}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Año:</span>{' '}
                    <span className="font-medium">{vehiculo.anio}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Kilómetros:</span>{' '}
                    <span className="font-medium">{vehiculo.km.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Combustible:</span>{' '}
                    <span className="font-medium">{vehiculo.tipoCombustible}</span>
                  </div>
                  {vehiculo.cilindrada && (
                    <div>
                      <span className="text-muted-foreground">Cilindrada:</span>{' '}
                      <span className="font-medium">{vehiculo.cilindrada}</span>
                    </div>
                  )}
                  {vehiculo.estadoGeneral && (
                    <div>
                      <span className="text-muted-foreground">Estado:</span>{' '}
                      <span className="font-medium">{vehiculo.estadoGeneral}</span>
                    </div>
                  )}
                  {vehiculo.unicoDueno !== null && vehiculo.unicoDueno !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Único dueño:</span>{' '}
                      <span className="font-medium">{vehiculo.unicoDueno ? 'Sí' : 'No'}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Modalidad:</span>{' '}
                    <span className="font-medium">{vehiculo.esSubasta ? 'Subasta' : 'Oferta directa'}</span>
                  </div>
                  {sucursal && (
                    <div>
                      <span className="text-muted-foreground">Sucursal:</span>{' '}
                      <span className="font-medium">{sucursal.nombre}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Autor:</span>{' '}
                    <span className="font-medium">{vehiculo.autor}</span>
                  </div>
                  {vehiculo.createdAt && (
                    <div>
                      <span className="text-muted-foreground">Creada:</span>{' '}
                      <span className="font-medium">
                        {format(new Date(vehiculo.createdAt), "dd/MM/yyyy HH:mm", { locale: es })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Fecha límite de ofertas */}
                {vehiculo.fechaLimiteOfertas && (
                  <div className="mt-4 p-3 rounded-md border bg-accent/30 flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground">Fecha límite de ofertas:</span>{' '}
                      <span className="font-medium">
                        {format(new Date(vehiculo.fechaLimiteOfertas), "dd/MM/yyyy HH:mm", { locale: es })}
                      </span>
                      {new Date(vehiculo.fechaLimiteOfertas) < new Date() && (
                        <Badge variant="destructive" className="ml-2 text-xs">Vencida</Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Datos del vendedor interno */}
                {(vehiculo.vendedorNombre || vehiculo.vendedorTelefono) && (
                  <div className="sm:col-span-2 mt-4 p-3 bg-accent/50 rounded-md border text-xs">
                    <div className="font-semibold mb-1 text-muted-foreground">Datos del Cliente (Uso interno):</div>
                    <div>
                      {vehiculo.vendedorNombre && <span className="font-medium mr-3">Nombre: {vehiculo.vendedorNombre}</span>}
                      {vehiculo.vendedorTelefono && <span className="font-medium">Teléfono: {vehiculo.vendedorTelefono}</span>}
                    </div>
                  </div>
                )}

                {vehiculo.comentarios && (
                  <div className="mt-4">
                    <span className="text-sm text-muted-foreground">Comentarios:</span>
                    <p className="text-sm mt-1">{vehiculo.comentarios}</p>
                  </div>
                )}

                {vehiculo.estadoPublicacion === 'En espera' && (
                  <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20">
                    <div className="flex items-center justify-between gap-2 text-amber-800 dark:text-amber-400 font-semibold mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        En Espera
                      </div>
                      {(user?.rol === 'Admin' || user?.rol === 'SuperAdmin') && (
                        <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => {
                          const dt = vehiculo.fechaEspera ? new Date(vehiculo.fechaEspera) : new Date();
                          const tzoffset = dt.getTimezoneOffset() * 60000;
                          setFechaEspera(new Date(dt - tzoffset).toISOString().slice(0, 16));
                          setMotivoEspera(vehiculo.motivoEspera || '');
                          setShowEsperaDialog(true);
                        }}>
                          <Edit className="h-3 w-3 mr-1" /> Editar
                        </Button>
                      )}
                    </div>
                    {vehiculo.fechaEspera && (
                      <div className="text-sm text-amber-700 dark:text-amber-500 mb-1">
                        <span className="font-medium">Hasta:</span> {format(new Date(vehiculo.fechaEspera), "dd/MM/yyyy HH:mm", { locale: es })}
                      </div>
                    )}
                    {vehiculo.motivoEspera && (
                      <div className="text-sm text-amber-700 dark:text-amber-500">
                        <span className="font-medium">Motivo:</span> {vehiculo.motivoEspera}
                      </div>
                    )}
                  </div>
                )}

                {vehiculo.estadoPublicacion === 'No adjudicada' && vehiculo.comentarioNoAdjudicada && (
                  <div className="mt-4 p-3 rounded-md border border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/10">
                    <div className="flex items-center gap-2 text-red-800 dark:text-red-400 font-semibold mb-2">
                      No Adjudicada
                    </div>
                    <div className="text-sm text-red-700 dark:text-red-300">
                      <span className="font-medium">Comentario:</span> {vehiculo.comentarioNoAdjudicada}
                    </div>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t">
                  <div className="text-2xl font-bold text-primary">
                    {vehiculo.precioBase ? `$${vehiculo.precioBase.toLocaleString('en-US')}` : 'Sin Base'}
                  </div>
                  {vehiculo.precioAdjudicado && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Adjudicado por: ${vehiculo.precioAdjudicado.toLocaleString('en-US')}
                    </div>
                  )}
                  {vehiculo.estadoPublicacion === 'Adjudicada' && vehiculo.fechaAdjudicacion && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Fecha de adjudicación: {new Date(vehiculo.fechaAdjudicacion).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Offers */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    Ofertas Recibidas
                    <Badge variant={vehiculo.deletedAt ? "destructive" : "secondary"} className="text-xs">
                      {ofertas.length}
                    </Badge>
                  </CardTitle>
                  {vehiculo.deletedAt && (
                    <span className="text-xs text-destructive font-medium flex items-center gap-1">
                      <Trash2 className="h-3 w-3" />
                      En papelera
                    </span>
                  )}
                </div>
                {vehiculo.deletedAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Se muestran las ofertas registradas para esta publicación eliminada.
                  </p>
                )}
              </CardHeader>
              <CardContent data-testid="offers-list">
                {ofertas.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No hay ofertas aún
                  </p>
                ) : (
                  <div className="space-y-3">
                    {ofertas.map((oferta) => {
                      const revendedor = revendedores[oferta.revendedorId];
                      const isOfertaDeleted = !!oferta.deletedAt;
                      return (
                        <Card key={oferta.id} className={`p-4 ${isOfertaDeleted ? 'bg-destructive/[0.03] border-destructive/30' : ''}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <button
                                className="font-medium text-left hover:text-primary transition-colors cursor-pointer underline-offset-2 hover:underline"
                                onClick={() => revendedor && setSelectedRevendedor(revendedor)}
                              >
                                {revendedor?.nombre || 'Revendedor'}
                                {revendedor?.deletedAt && (
                                  <span className="ml-2 text-xs text-muted-foreground" title={`Eliminado el ${new Date(revendedor.deletedAt).toLocaleDateString('es-UY')}`}>
                                    (eliminado)
                                  </span>
                                )}
                              </button>
                              {revendedor?.empresa && (
                                <div className="text-xs text-muted-foreground">
                                  {revendedor.empresa}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isOfertaDeleted ? (
                                <>
                                  <Badge variant="destructive" className="gap-1 text-[10px] h-5">
                                    <Trash2 className="h-3 w-3" />
                                    Eliminada
                                  </Badge>
                                  {isAdmin && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[10px] gap-1 px-2"
                                      onClick={() => handleRestoreOffer(oferta.id)}
                                      title="Restaurar oferta"
                                    >
                                      <RotateCcw className="h-3 w-3 text-primary" />
                                      Restaurar
                                    </Button>
                                  )}
                                  {isSuperAdmin && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                      onClick={() => handlePermanentDeleteOffer(oferta.id)}
                                      title="Eliminar oferta definitivamente (SuperAdmin)"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <>
                                  {isSuperAdmin && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSoftDeleteOffer(oferta.id);
                                      }}
                                      title="Mover oferta a papelera"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {(!vehiculo.deletedAt && (user?.rol === 'SuperAdmin' || user?.rol === 'Admin')) ? (
                                    <Select value={oferta.estado} onValueChange={(val) => handleSuperAdminOfferStatusChange(oferta.id, val)}>
                                      <SelectTrigger className={`h-6 text-[10px] w-[100px] border-0 focus:ring-0 ${oferta.estado === 'Aceptada' ? 'border bg-background' : oferta.estado === 'Rechazada' ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground'}`}>
                                        <SelectValue placeholder="Estado" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Pendiente">Pendiente</SelectItem>
                                        <SelectItem value="Aceptada">Aceptada</SelectItem>
                                        <SelectItem value="Rechazada">Rechazada</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge variant={badgeVariants[oferta.estado]}>
                                      {oferta.estado}
                                    </Badge>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          <div className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
                            ${oferta.monto.toLocaleString('en-US')}
                            {oferta.esManual && (
                              <Badge variant="outline" className="gap-1 text-[11px] h-5 border-primary text-primary">
                                <Hand className="h-3 w-3" />
                                Manual
                              </Badge>
                            )}
                          </div>

                          <div className="text-xs text-muted-foreground mb-3">
                            <div>Ofrecido el: {format(new Date(oferta.hora), 'dd/MM/yyyy HH:mm')}</div>
                            {isOfertaDeleted ? (
                              <div className="text-destructive font-medium text-xs flex items-center gap-1 mt-1">
                                <Trash2 className="h-3 w-3" />
                                Eliminada el: {format(new Date(oferta.deletedAt), 'dd/MM/yyyy HH:mm')}
                              </div>
                            ) : oferta.estado === 'Aceptada' && oferta.fechaAceptacion ? (
                              <div className="mt-1 text-green-600 dark:text-green-400 font-medium">
                                Aceptada el: {format(new Date(oferta.fechaAceptacion), 'dd/MM/yyyy HH:mm')}
                              </div>
                            ) : null}
                          </div>

                          {!vehiculo.deletedAt && !isOfertaDeleted && oferta.estado === 'Pendiente' && vehiculo.estadoPublicacion !== 'Adjudicada' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                className="flex-1"
                                onClick={() => {
                                  setOfertaToAccept(oferta.id);
                                  const defaultDate = new Date();
                                  defaultDate.setHours(defaultDate.getHours() + 48); // default to 48h
                                  const tzoffset = defaultDate.getTimezoneOffset() * 60000;
                                  const localISOTime = new Date(defaultDate - tzoffset).toISOString().slice(0, 16);
                                  setFechaEspera(localISOTime);
                                  setMotivoEspera('');
                                  setShowEsperaDialog(true);
                                }}
                                disabled={!!processingOffer}
                                data-testid="accept-offer-button"
                              >
                                {processingOffer === oferta.id ? (
                                  <><div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-1" />Procesando...</>
                                ) : (
                                  <><Check className="h-4 w-4 mr-1" />Aceptar</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setOfertaToReject(oferta.id)}
                                disabled={!!processingOffer}
                                data-testid="reject-offer-button"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Rechazar
                              </Button>
                            </div>
                          )}

                          {revendedor && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full mt-2"
                              onClick={() => setSelectedRevendedor(revendedor)}
                            >
                              <User className="h-4 w-4 mr-2" />
                              Ver Revendedor
                            </Button>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Dialog Revendedores que Interactuaron */}
      <Dialog open={showInteraccionesDialog} onOpenChange={setShowInteraccionesDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <MessageCircle className="h-5 w-5 text-green-600" />
              Revendedores que Interactuaron
            </DialogTitle>
            <DialogDescription>
              {vehiculo?.marca} {vehiculo?.modelo} {vehiculo?.anio} •{' '}
              {interaccionesData.length} {interaccionesData.length === 1 ? 'revendedor único' : 'revendedores únicos'}
            </DialogDescription>
          </DialogHeader>

          {/* Buscador interno */}
          <div className="relative my-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por revendedor, empresa o teléfono..."
              value={searchInteraccion}
              onChange={(e) => setSearchInteraccion(e.target.value)}
              className="pl-9 text-sm"
              data-testid="search-interacciones-input"
            />
          </div>

          {/* Contenido / Listado */}
          <div className="flex-1 overflow-y-auto pr-1 space-y-3 min-h-[220px]">
            {loadingInteracciones ? (
              <div className="py-12 flex flex-col items-center justify-center text-muted-foreground">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mb-3" />
                <p className="text-sm">Cargando revendedores que interactuaron...</p>
              </div>
            ) : filteredInteracciones.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <p className="text-sm">
                  {searchInteraccion ? 'No se encontraron revendedores con ese filtro.' : 'No hay interacciones registradas para este vehículo.'}
                </p>
              </div>
            ) : (
              filteredInteracciones.map((item, idx) => {
                const rev = item.revendedor;
                const nombreMostrar = rev?.nombre || item.nombreContacto || 'Contacto WhatsApp';
                const empresaMostrar = rev?.empresa;
                const tieneOferta = item.hizoOferta;
                const telefonoLimpio = item.telefono?.replace(/\D/g, '');

                return (
                  <div
                    key={item.telefono || idx}
                    className="p-3.5 rounded-lg border bg-card hover:bg-accent/40 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                        {nombreMostrar.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-sm">
                            {nombreMostrar}
                          </span>
                          {rev?.deletedAt && (
                            <Badge variant="destructive" className="text-[10px] h-4 px-1.5 font-normal">
                              Eliminado
                            </Badge>
                          )}
                          {tieneOferta ? (
                            <Badge variant="default" className="text-[10px] h-4 px-1.5 bg-green-600 hover:bg-green-700 font-normal">
                              Ofertó (${item.montoOfertaMasAlta?.toLocaleString('en-US')})
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5 text-muted-foreground font-normal">
                              Sin oferta
                            </Badge>
                          )}
                        </div>

                        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                          {empresaMostrar && (
                            <span className="flex items-center gap-1 font-medium text-foreground/80">
                              <Building2 className="h-3 w-3 text-muted-foreground" />
                              {empresaMostrar}
                            </span>
                          )}
                          {item.telefono && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {item.telefono}
                            </span>
                          )}
                          {item.totalMensajes > 0 && (
                            <span className="text-muted-foreground">
                              • {item.totalMensajes} {item.totalMensajes === 1 ? 'mensaje' : 'mensajes'}
                            </span>
                          )}
                        </div>

                        {item.ultimoMensaje && (
                          <div className="text-xs text-muted-foreground mt-1.5 bg-muted/50 p-1.5 rounded line-clamp-1 italic">
                            "{item.ultimoMensaje}"
                          </div>
                        )}
                        {item.ultimaFecha && (
                          <div className="text-[11px] text-muted-foreground mt-1">
                            Última actividad: {format(new Date(item.ultimaFecha), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      {rev && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs gap-1.5"
                          onClick={() => {
                            setSelectedRevendedor(rev);
                          }}
                        >
                          <User className="h-3.5 w-3.5" />
                          Ver ficha
                        </Button>
                      )}
                      {telefonoLimpio && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs gap-1.5 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30"
                          onClick={() => window.open(`https://wa.me/${telefonoLimpio}`, '_blank')}
                          title="Abrir WhatsApp"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          WhatsApp
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter className="pt-2 border-t mt-2">
            <Button variant="outline" onClick={() => setShowInteraccionesDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revendedor Detail Dialog */}
      <Dialog open={!!selectedRevendedor} onOpenChange={() => setSelectedRevendedor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {selectedRevendedor?.nombre}
            </DialogTitle>
          </DialogHeader>
          {selectedRevendedor && (
            <div className="space-y-4">
              <div className="grid gap-3 text-sm">
                {selectedRevendedor.empresa && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <div className="text-xs text-muted-foreground">Empresa</div>
                      <div className="font-medium">{selectedRevendedor.empresa}</div>
                    </div>
                  </div>
                )}
                {selectedRevendedor.whatsapp && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-muted-foreground">WhatsApp</div>
                      <div className="font-medium">{selectedRevendedor.whatsapp}</div>
                    </div>
                    <a
                      href={formatWhatsappLink(selectedRevendedor.whatsapp)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button size="sm" variant="outline" className="gap-1.5">
                        <MessageCircle className="h-3.5 w-3.5" />
                        Abrir
                      </Button>
                    </a>
                  </div>
                )}
                {selectedRevendedor.ciudad && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <div className="text-xs text-muted-foreground">Ciudad</div>
                      <div className="font-medium">{selectedRevendedor.ciudad}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                  <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <div className="text-xs text-muted-foreground">Estado</div>
                    <div className="font-medium">
                      <Badge variant={selectedRevendedor.estado === 'Activo' ? 'default' : 'destructive'}>
                        {selectedRevendedor.estado}
                      </Badge>
                      {selectedRevendedor.deletedAt && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          (Eliminado el {new Date(selectedRevendedor.deletedAt).toLocaleDateString('es-UY')})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {selectedRevendedor.rating != null && (
                  <div className="flex items-center gap-3 p-3 rounded-md bg-accent/30">
                    <span className="text-muted-foreground flex-shrink-0">⭐</span>
                    <div>
                      <div className="text-xs text-muted-foreground">Rating</div>
                      <div className="font-medium">{selectedRevendedor.rating}/5</div>
                    </div>
                  </div>
                )}
              </div>
              {selectedRevendedor.etiquetas && selectedRevendedor.etiquetas.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground mb-2">Etiquetas</div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedRevendedor.etiquetas.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {selectedRevendedor.notas && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Notas</div>
                  <p className="text-sm p-3 rounded-md bg-muted">{selectedRevendedor.notas}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept Offer Dialog replaced by Espera Dialog */}

      {/* Reject Offer Dialog */}
      <AlertDialog open={!!ofertaToReject} onOpenChange={() => setOfertaToReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Rechazar oferta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción marcará la oferta como rechazada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleRejectOffer(ofertaToReject)}>
              Rechazar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Espera Dialog */}
      <Dialog open={showEsperaDialog} onOpenChange={(open) => {
        setShowEsperaDialog(open);
        if (!open) setOfertaToAccept(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pasar a "En espera"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha y hora estimada</label>
              <input
                type="datetime-local"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={fechaEspera}
                onChange={(e) => setFechaEspera(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Motivo / Comentario</label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Ej. Esperando respuesta del revendedor X..."
                value={motivoEspera}
                onChange={(e) => setMotivoEspera(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEsperaDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handlePasarAEspera}>
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* No Adjudicada Dialog */}
      <Dialog open={showNoAdjudicadoDialog} onOpenChange={setShowNoAdjudicadoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Marcar como No adjudicada?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Esta acción finalizará la publicación sin que haya sido adjudicada a ningún ofertante.
            </p>
            {(!isVencida || ofertas.length > 0) && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Comentario / Motivo (Obligatorio)</label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={!isVencida ? 'Explique el motivo por el cual se cierra la publicación antes de su vencimiento...' : 'Explique el motivo por el cual no se adjudicó a pesar de tener ofertas...'}
                  value={comentarioNoAdjudicada}
                  onChange={(e) => setComentarioNoAdjudicada(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowNoAdjudicadoDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleNoAdjudicado}>
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjudicar Dialog */}
      <Dialog open={showAdjudicarDialog} onOpenChange={setShowAdjudicarDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Adjudicación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <p className="text-sm text-muted-foreground">
              ¿Está seguro de que desea marcar este vehículo como adjudicado?
            </p>
            {ofertas.filter(o => o.estado === 'Aceptada').length > 0 ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md dark:bg-green-900/20 dark:border-green-800">
                <div className="text-sm font-medium text-green-800 dark:text-green-400 mb-2">Oferta a adjudicar:</div>
                {ofertas.filter(o => o.estado === 'Aceptada').map(oferta => {
                  const revendedor = revendedores[oferta.revendedorId];
                  return (
                    <div key={oferta.id} className="text-sm">
                      <span className="font-semibold text-green-900 dark:text-green-300">
                        ${oferta.monto.toLocaleString('en-US')} USD
                      </span>
                      <span className="text-muted-foreground ml-2">
                        por {revendedor?.nombre || 'Revendedor'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800 text-sm text-red-800 dark:text-red-400">
                ⚠️ No hay ninguna oferta en estado "Aceptada". Debe aceptar una oferta antes de adjudicar.
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-2">
              Esta acción notificará automáticamente al ganador y rechazará todas las ofertas pendientes restantes.
            </p>
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAdjudicarDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAdjudicar} 
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={ofertas.filter(o => o.estado === 'Aceptada').length === 0}
            >
              Confirmar Adjudicación
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reenviar Dialog */}
      <Dialog open={showReenviarDialog} onOpenChange={setShowReenviarDialog}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Reenviar Publicación</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4 flex-1 overflow-hidden">
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSelectBidders}
                className="text-xs"
              >
                Seleccionar Solo Con Oferta
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedRevendedoresReenviar([])}
                className="text-xs"
              >
                Limpiar Selección
              </Button>
            </div>

            <div className="border rounded-md p-2 overflow-y-auto max-h-[45vh] space-y-1">
              {Object.values(revendedores)
                .filter(isReenviarCandidate)
                .map((r) => {
                  const hasOffered = ofertas.some(o => o.revendedorId === r.id);
                  return (
                    <div
                      key={r.id}
                      className="flex items-center justify-between p-2 hover:bg-accent/50 rounded-sm cursor-pointer border-b last:border-0 border-border/50"
                      onClick={() => toggleRevendedorSelection(r.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedRevendedoresReenviar.includes(r.id)}
                          onChange={() => { }} // toggled by parent div
                          className="h-4 w-4 rounded border-gray-300 pointer-events-none"
                        />
                        <div>
                          <p className="text-sm font-medium leading-none">{r.nombre}</p>
                          {r.empresa && <p className="text-xs text-muted-foreground mt-1">{r.empresa}</p>}
                        </div>
                      </div>
                      {hasOffered && <Badge variant="secondary" className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 hover:bg-amber-500/20">Ofertó</Badge>}
                    </div>
                  );
                })}
            </div>
            <div className="text-sm text-muted-foreground">
              {selectedRevendedoresReenviar.length} seleccionados
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t mt-auto">
            <Button variant="outline" onClick={() => setShowReenviarDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleReenviar} disabled={reenviando || selectedRevendedoresReenviar.length === 0}>
              {reenviando ? 'Enviando...' : 'Reenviar Ahora'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extender Dialog */}
      <Dialog open={showExtenderDialog} onOpenChange={setShowExtenderDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Extender Plazo de Ofertas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Esta acción extenderá el tiempo disponible para recibir ofertas y notificará a todos los revendedores que ya hayan ofertado.
            </p>
            {isVencida && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-400">
                ⚠️ La publicación ya está vencida. La extensión de tiempo se calculará a partir de este momento.
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Horas a extender</label>
              <Input
                type="number"
                min="1"
                value={horasExtender}
                onChange={(e) => setHorasExtender(e.target.value)}
                placeholder="Ej. 10"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 text-sm text-muted-foreground pt-4 border-t">
            <Button variant="outline" onClick={() => setShowExtenderDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleExtender} 
              disabled={extendiendo || !horasExtender || horasExtender < 1}
            >
              {extendiendo ? 'Extendiendo...' : 'Extender y Notificar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <NewManualOfferDialog
        open={showNewOfferDialog}
        onOpenChange={setShowNewOfferDialog}
        onSuccess={loadData}
        revendedores={revendedores}
        preselectedVehiculo={vehiculo}
      />

      {/* Edit Marca/Modelo Dialog */}
      <Dialog open={showEditMarcaModelo} onOpenChange={setShowEditMarcaModelo}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Marca y Modelo</DialogTitle>
            <DialogDescription>
              Seleccione la marca y modelo correctos para esta publicación
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <MarcaModeloSelector
              marca={editMarca}
              modelo={editModelo}
              onMarcaChange={(val) => { setEditMarca(val); if (val !== editMarca) setEditModelo(''); }}
              onModeloChange={(val) => setEditModelo(val)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMarcaModelo(false)} disabled={savingMarcaModelo}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!editMarca || !editModelo) {
                  toast.error('Marca y modelo son requeridos');
                  return;
                }
                setSavingMarcaModelo(true);
                try {
                  await axios.put(`${API}/vehiculos/${id}`, { marca: editMarca, modelo: editModelo });
                  toast.success('Marca y modelo actualizados');
                  setShowEditMarcaModelo(false);
                  loadData();
                } catch (error) {
                  console.error('Error updating marca/modelo:', error);
                  toast.error('Error al actualizar marca y modelo');
                } finally {
                  setSavingMarcaModelo(false);
                }
              }}
              disabled={savingMarcaModelo}
              data-testid="save-edit-marca-modelo"
            >
              {savingMarcaModelo ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DetallePublicacion;